/**
 * WHEP (WebRTC-HTTP Egress Protocol) client — minimal, dependency-free.
 *
 * Per IETF draft-ietf-wish-whep:
 *
 *   1. Client `POST <whep-url>` with `Content-Type: application/sdp`
 *      and body = local SDP offer.
 *   2. Server responds `201 Created`, body = SDP answer,
 *      `Location` header = resource URL for teardown.
 *   3. Client may `DELETE <resource-url>` to gracefully release the
 *      consumer slot on the publisher (MediaMTX, go2rtc, etc.).
 *
 * Lives in `@cguard/api-client` rather than in the operator-console app
 * so the global `fetch` ban (oxlint `no-restricted-globals` in apps/**)
 * doesn't force us into a JSON-only `cguardFetcher` detour — WHEP
 * traffics in SDP plaintext, not JSON, so the JSON-defaults fetcher
 * would silently mangle the answer body.
 *
 * Used by `apps/operator-console/app/components/whep-video-player.tsx`
 * (Day 7 release-gate G4); the smoke surface in
 * `tools/edge-debug-viewer/index.html` predates this helper and
 * inlines the same pattern for the SSH-port-forward debugging flow.
 */

import { getCguardClientConfig } from '../runtime/fetcher';

export interface WhepExchangeOptions {
  /** Local SDP offer to send to the WHEP endpoint. */
  readonly offerSdp: string;
  /** Optional bearer token; attached as `Authorization: Bearer <token>` when present. */
  readonly bearerToken?: string | null | undefined;
  /** Optional AbortSignal so the player effect can cancel on unmount. */
  readonly signal?: AbortSignal;
}

export interface WhepExchangeResult {
  /** SDP answer from the WHEP server — feed to `RTCPeerConnection.setRemoteDescription`. */
  readonly answerSdp: string;
  /**
   * Resolved resource URL the server returned in `Location` (per WHEP
   * spec), or `null` if the server omitted it. Pass to
   * {@link deleteWhepResource} on teardown.
   */
  readonly resourceUrl: string | null;
}

export interface CentralWhepExchangeOptions {
  /** Local SDP offer to authorize and exchange through Central. */
  readonly offerSdp: string;
  /** Current CGuard access token. Central signalling is never anonymous. */
  readonly bearerToken: string;
  readonly signal?: AbortSignal;
}

export interface CentralWhepSessionResult {
  readonly answerSdp: string;
  /** Opaque central handle extracted from the same-origin Location header. */
  readonly handle: string;
}

export interface CentralWhepTrickleOptions {
  readonly sdpFragment: string;
  readonly bearerToken: string;
  readonly signal?: AbortSignal;
}

export interface CentralWhepDeleteOptions {
  readonly bearerToken: string;
  readonly signal?: AbortSignal;
}

export class WhepError extends Error {
  override readonly name = 'WhepError';
  constructor(
    /** Stable kind so callers can branch on error category. */
    readonly kind: 'sdp' | 'network' | 'config',
    message: string,
    readonly httpStatus?: number,
  ) {
    super(message);
  }
}

/**
 * Send an SDP offer to a WHEP endpoint and return the server's answer.
 *
 * @throws {@link WhepError} for any non-2xx response or fetch failure.
 */
export async function exchangeWhepOffer(
  url: string,
  opts: WhepExchangeOptions,
): Promise<WhepExchangeResult> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/sdp',
    Accept: 'application/sdp',
  };
  if (opts.bearerToken !== undefined && opts.bearerToken !== null && opts.bearerToken.length > 0) {
    headers.Authorization = `Bearer ${opts.bearerToken}`;
  }

  let resp: Response;
  try {
    resp = await fetch(url, {
      method: 'POST',
      headers,
      body: opts.offerSdp,
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (err) {
    if (err instanceof Error) {
      // fetch network failures throw `TypeError: Failed to fetch`.
      throw new WhepError('network', err.message);
    }
    throw new WhepError('network', String(err));
  }

  if (!resp.ok) {
    let body = '';
    try {
      body = await resp.text();
    } catch {
      // ignore; pass through what we have.
    }
    throw new WhepError(
      'sdp',
      `WHEP ${resp.status} ${resp.statusText}${body ? ` — ${body.slice(0, 200)}` : ''}`,
      resp.status,
    );
  }

  const location = resp.headers.get('Location');
  let resourceUrl: string | null = null;
  if (location !== null && location.length > 0) {
    try {
      resourceUrl = new URL(location, url).toString();
    } catch {
      resourceUrl = null;
    }
  }

  const answerSdp = await resp.text();
  return { answerSdp, resourceUrl };
}

/** Exchange an SDP offer through the authenticated Cameras WHEP proxy. */
export async function exchangeCentralWhepOffer(
  cameraId: string,
  opts: CentralWhepExchangeOptions,
): Promise<CentralWhepSessionResult> {
  const token = requireBearerToken(opts.bearerToken);
  const path = `/v1/cameras/${encodeURIComponent(cameraId)}/whep`;
  const url = centralUrl(path);
  const response = await centralFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/sdp',
      Accept: 'application/sdp',
      Authorization: `Bearer ${token}`,
    },
    body: opts.offerSdp,
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  await requireSuccess(response, 'offer');
  if (response.status !== 201) {
    throw new WhepError(
      'sdp',
      `Central WHEP offer returned HTTP ${response.status}.`,
      response.status,
    );
  }

  const handle = extractCentralHandle(response.headers.get('Location'), url);
  const answerSdp = await response.text();
  if (answerSdp.trim().length === 0) {
    throw new WhepError('sdp', 'Central WHEP offer returned an empty SDP answer.', response.status);
  }
  return { answerSdp, handle };
}

/** Forward an ICE-trickle fragment through an authenticated central handle. */
export async function patchCentralWhepSession(
  handle: string,
  opts: CentralWhepTrickleOptions,
): Promise<void> {
  const response = await centralFetch(centralSessionUrl(handle), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/trickle-ice-sdpfrag',
      Authorization: `Bearer ${requireBearerToken(opts.bearerToken)}`,
    },
    body: opts.sdpFragment,
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  await requireSuccess(response, 'trickle');
}

/** Tear down an authenticated central WHEP session. */
export async function deleteCentralWhepSession(
  handle: string,
  opts: CentralWhepDeleteOptions,
): Promise<void> {
  const response = await centralFetch(centralSessionUrl(handle), {
    method: 'DELETE',
    keepalive: true,
    headers: {
      Authorization: `Bearer ${requireBearerToken(opts.bearerToken)}`,
    },
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  await requireSuccess(response, 'teardown');
}

function requireBearerToken(value: string): string {
  const token = value.trim();
  if (token.length === 0) {
    throw new WhepError('config', 'Central WHEP signalling requires an access token.');
  }
  return token;
}

function centralSessionUrl(handle: string): string {
  if (!isOpaqueCentralHandle(handle)) {
    throw new WhepError('config', 'Central WHEP returned an invalid session handle.');
  }
  return centralUrl(`/v1/cameras/whep-sessions/${encodeURIComponent(handle)}`);
}

function centralUrl(path: string): string {
  const baseUrl = getCguardClientConfig().baseUrl.trim();
  if (baseUrl.length === 0) return path;
  return `${baseUrl.replace(/\/$/, '')}${path}`;
}

async function centralFetch(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (error) {
    throw new WhepError('network', error instanceof Error ? error.message : String(error));
  }
}

async function requireSuccess(response: Response, operation: string): Promise<void> {
  if (response.ok) return;
  let detail = '';
  try {
    detail = await response.text();
  } catch {
    // The HTTP status remains sufficient when the body cannot be read.
  }
  throw new WhepError(
    'sdp',
    `Central WHEP ${operation} failed with HTTP ${response.status}${detail ? ` — ${detail.slice(0, 200)}` : ''}`,
    response.status,
  );
}

function extractCentralHandle(locationHeader: string | null, requestUrl: string): string {
  if (!locationHeader) {
    throw new WhepError('sdp', 'Central WHEP offer omitted its session Location header.');
  }

  const expectedPrefix = '/v1/cameras/whep-sessions/';
  let pathname: string;
  let search = '';
  let hash = '';
  if (/^https?:\/\//i.test(locationHeader)) {
    const request = absoluteRequestUrl(requestUrl);
    const location = new URL(locationHeader);
    if (!request || location.origin !== request.origin) {
      throw new WhepError('sdp', 'Central WHEP returned an off-origin session Location.');
    }
    ({ pathname, search, hash } = location);
  } else if (locationHeader.startsWith('/')) {
    const parsed = new URL(locationHeader, 'https://cguard.invalid');
    ({ pathname, search, hash } = parsed);
  } else {
    throw new WhepError('sdp', 'Central WHEP returned an invalid session Location.');
  }

  const encodedHandle = pathname.startsWith(expectedPrefix)
    ? pathname.slice(expectedPrefix.length)
    : '';
  let handle = '';
  try {
    handle = decodeURIComponent(encodedHandle);
  } catch {
    throw new WhepError('sdp', 'Central WHEP returned an invalid session Location.');
  }
  if (search || hash || !isOpaqueCentralHandle(handle)) {
    throw new WhepError('sdp', 'Central WHEP returned an invalid session Location.');
  }
  return handle;
}

function absoluteRequestUrl(requestUrl: string): URL | null {
  try {
    if (/^https?:\/\//i.test(requestUrl)) return new URL(requestUrl);
    if (typeof location !== 'undefined') return new URL(requestUrl, location.origin);
  } catch {
    // Invalid request context fails the same-origin check below.
  }
  return null;
}

function isOpaqueCentralHandle(value: string): boolean {
  return value.length > 0 && value.length <= 4096 && /^[A-Za-z0-9._-]+$/.test(value);
}

/**
 * Best-effort graceful teardown of a WHEP session. Fires `DELETE` at
 * the resource URL the server returned on `POST`, with `keepalive: true`
 * so it survives page-unload races. Returns the underlying promise so
 * callers in long-lived contexts (player effects, not unload handlers)
 * can chain on it, but the operator-console camera tile uses
 * fire-and-forget — teardown failure is non-fatal because MediaMTX
 * reaps idle consumer slots on its own timeout.
 *
 * The optional {@link signal} lets a long-lived caller (a player effect
 * holding a grid-level AbortController) cancel an in-flight DELETE if a
 * later layout flip supersedes it — rapid 1-up → 16-up → 1-up flips
 * could otherwise queue up dozens of unaborted DELETEs against MediaMTX.
 * The two flags interact cleanly per the Fetch spec: `keepalive: true`
 * guarantees survival across page-unload, while `signal` is honoured
 * for explicit programmatic cancellation in the live-document case.
 */
export function deleteWhepResource(resourceUrl: string, signal?: AbortSignal): Promise<void> {
  const init: RequestInit = { method: 'DELETE', keepalive: true };
  if (signal !== undefined) init.signal = signal;
  return fetch(resourceUrl, init)
    .then(() => undefined)
    .catch(() => {
      // Swallow — see commentary above. AbortError lands here too,
      // which is the intended behaviour: an aborted teardown is not
      // a caller-actionable failure.
    });
}
